import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Alert } from '@lms/ui';
import { useAuth } from '@/hooks/useAuth';
import { ApiRequestError } from '@/lib/api';

const LOGIN_HERO_IMAGE = '/images/login-hero.jpg';

interface LoginForm {
  email: string;
  password: string;
  remember?: boolean;
}

export function LoginPage() {
  const { login, isLoggingIn, loginError } = useAuth();
  const { register, handleSubmit } = useForm<LoginForm>();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = handleSubmit(async (values) => {
    await login({ email: values.email, password: values.password });
  });

  const message = loginError instanceof ApiRequestError ? loginError.message : loginError?.message;

  return (
    <main className="lms-login">
      <div className="lms-login__hero" aria-hidden="true">
        <img
          className="lms-login__hero-image"
          src={LOGIN_HERO_IMAGE}
          alt=""
        />
        <div className="lms-login__hero-overlay" />
      </div>

      <div className="lms-login__panel">
        <div className="lms-login__mobile-header">
          <div className="lms-login__mobile-brand">
            <div className="lms-login__mobile-logo">
              <span className="material-symbols-outlined" aria-hidden="true">
                directions_car
              </span>
            </div>
            <span className="lms-login__mobile-title">AutoEscuela Digital</span>
          </div>
          <div className="lms-login__secure-badge">
            <span className="material-symbols-outlined" aria-hidden="true">
              lock
            </span>
            <span>Acceso Seguro</span>
          </div>
        </div>

        <div className="lms-login__content">
          <div className="lms-login__intro">
            <h1 className="lms-login__title">Iniciar sesión</h1>
            <p className="lms-login__subtitle">Ingresa tus credenciales para iniciar sesión</p>
          </div>

          {message && (
            <Alert variant="danger" className="lms-login__error">
              {message}
            </Alert>
          )}

          <form onSubmit={onSubmit} className="lms-login__form">
            <div className="lms-login__field">
              <label className="lms-login__label" htmlFor="identifier">
                Correo electrónico
              </label>
              <div className="lms-login__input-wrap">
                <span className="lms-login__input-icon material-symbols-outlined" aria-hidden="true">
                  mail
                </span>
                <input
                  id="identifier"
                  type="text"
                  autoComplete="username"
                  className="lms-login__input"
                  placeholder="ej. alumno@correo.com"
                  required
                  {...register('email', { required: true })}
                />
              </div>
            </div>

            <div className="lms-login__field">
              <label className="lms-login__label" htmlFor="password">
                Contraseña
              </label>
              <div className="lms-login__input-wrap">
                <span className="lms-login__input-icon material-symbols-outlined" aria-hidden="true">
                  lock
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="lms-login__input lms-login__input--password"
                  placeholder="••••••••••••"
                  required
                  {...register('password', { required: true })}
                />
                <button
                  type="button"
                  className="lms-login__toggle-password"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label="Mostrar u ocultar contraseña"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <div className="lms-login__remember">
              <label className="lms-login__remember-label">
                <input
                  id="remember"
                  type="checkbox"
                  className="lms-login__remember-input"
                  {...register('remember')}
                />
                <span>Recordar mi sesión en este dispositivo</span>
              </label>
            </div>

            <button type="submit" className="lms-login__submit" disabled={isLoggingIn}>
              <span>{isLoggingIn ? 'Iniciando sesión…' : 'Iniciar sesión'}</span>
              <span className="material-symbols-outlined" aria-hidden="true">
                login
              </span>
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
